/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { getTotalPages } from "selectors/documentSelectors";
import { getDocumentVisibleHeight } from "selectors/documentViewSelectors";
import { inject } from "mobx-react";
import { HUDView } from "./HUDView";
import { isSearchFeatureEnabled } from "selectors/appSelectors";
import {
  showHUD,
  showPageNumber,
  showZoomUI,
  getShowZoomInOut,
  showPrint,
  showDownloadButton,
  showDockButton,
  getShowSearchInHUD,
  getIsSearchInputUIVisibleInHUD,
  getHUDLeft,
  getIsHUDDocked,
  hudDisabled,
  getDockedWidth,
  showPageNavigation,
  showOverflowMenuButton,
  showRotateViewButton,
  getShouldUpdateHUDPosition,
  applyAnimationClasses,
  animationOutDuration,
  animationInDuration,
  shouldAutoHideHud,
  getIsHUDLargerThanViewPort,
  getAnimationType
} from "selectors/hudSelectors";
import { getIsImmersiveMode } from "selectors/appUiStateSelectors";
import { hudUiControlAlignments } from "constants/enums";
import { touchManager } from "../../touchManager";
import { getShouldUseModernViewer } from "selectors/appSelectors";
import { getPagesViewYPos } from "selectors/pagedViewState";
import { init, cleanup } from "services/NavigationBarService";

const mapStoreToProps = (stores, ownProps) => {
  const hudStore = stores.hudStore;
  return {
    totalPages: getTotalPages(stores),
    enableSearchFeature: isSearchFeatureEnabled(stores),
    setHudDisable: stores.hudStore.setHudDisable,
    isHUDEnabled: !hudDisabled(stores),
    showHUD: showHUD(stores),
    showPageNavigation: showPageNavigation(stores),
    showPageNumber: showPageNumber(stores),
    showZoomUI: showZoomUI(stores),
    showZoomInOut: getShowZoomInOut(stores),
    showPrint: showPrint(stores),
    enableRotation: getIsHUDLargerThanViewPort(stores) && showRotateViewButton(stores),
    applyAnimationClasses: applyAnimationClasses(stores),
    animationInDuration: animationInDuration(stores),
    animationOutDuration: animationOutDuration(stores),
    animationType: getAnimationType(stores),
    showDownloadButton: showDownloadButton(stores),
    showDockButton: showDockButton(stores),
    setFocusOnHud: hudStore.setFocus,
    showOverflowMenuButton: showOverflowMenuButton(stores),
    customizeHudComponent: hudStore.customizeHudComponent,
    setPageNavigationVisibility: hudStore.setPageNavigationVisibility,
    setPageNumberVisibility: hudStore.setPageNumberVisibility,
    setZoomUIVisibility: hudStore.setZoomUIVisibility,
    setZoomControlsVisibility: hudStore.setZoomControlsVisibility,
    setFitWidthModeVisibility: hudStore.setFitWidthModeVisibility,
    setFitPageModeVisibility: hudStore.setFitPageModeVisibility,
    setFixedWidthVisibility: hudStore.setFixedWidthVisibility,
    setPrintVisibility: hudStore.setPrintVisibility,
    setRotateViewButtonVisibility: hudStore.setRotateViewButtonVisibility,
    isDocked: getIsHUDDocked(stores),
    isImmersiveMode: getIsImmersiveMode(stores),
    setHUDView: ownProps.setHUDView,
    onTouchMove: event => {
      touchManager.disableTouchEvent(event);
    },
    init,
    cleanup,
    showSearchUI: getShowSearchInHUD(stores),
    left: getHUDLeft(stores),
    docVisibleHeight: getDocumentVisibleHeight(stores) + getPagesViewYPos(stores),
    dockedWidth: getDockedWidth(stores),
    disableContextMenu: hudClassName => {
      stores.contextMenuStore.addClassNameToDisableContextMenuOn(hudClassName);
    },
    setHUDWidth: hudWidth => {
      stores.hudStore.setHUDWidth(hudWidth);
    },
    getExternalComponentsFromAlignment: alignment => {
      let components = [];
      if (alignment === hudUiControlAlignments.LEFT) {
        components = stores.hudStore.getLeftAlignedExternalComponents;
      } else if (alignment === hudUiControlAlignments.RIGHT) {
        components = stores.hudStore.getRightAlignedExternalComponents;
      } else if (alignment === hudUiControlAlignments.CENTER) {
        components = stores.hudStore.getCenterAlignedExternalComponents;
      }
      return components;
    },
    isAutoHiding: shouldAutoHideHud(stores),
    externalComponents: stores.hudStore.externalComponentsIds.peek(),
    shouldUpdateHUDPosition: getShouldUpdateHUDPosition(stores),
    getIsSearchInputUIVisibleInHUD: getIsSearchInputUIVisibleInHUD(stores),
    useModernViewer: getShouldUseModernViewer(stores)
  };
};

const HUDContainer = inject(mapStoreToProps)(HUDView);

export { HUDContainer };
